import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { UNDO_OFFER_MS, mayMarkReceived } from '@/domain';
import { useMarkDelivered, useUnmarkReceived } from '@/application/usecases/useOrders';
import { useSession } from '@/application/usecases/useSession';
import { failureWords } from '@/shared/failure/failure';
import { strings } from '@/shared/strings';
import { toast } from '@/ui/primitives/toastStore';
import { DeliveredDialog } from '../orders/DeliveredDialog';

/** What marking a case received needs to know of it, whichever screen it is marked from. */
export interface ReceivedCase {
  jobOrderNo: string;
  /** The order's, sent as If-Match, so a case somebody changed in the meantime is refused. */
  rowVersion: string;
  /** When it left the lab, so a day before it is refused in the sheet as on the server. */
  dispatchedAt: string | null;
}

export interface MarkReceived {
  /**
   * Whether this person is offered Mark received at all: `mayMarkReceived` in
   * the domain, a holder of `ship` who is not an accountant kept to their own
   * three screens. The button is drawn only when this is true.
   */
  allowed: boolean;
  /** Opens the confirmation for this case. */
  ask: (target: ReceivedCase) => void;
  /** The confirmation while it is open. The screen draws it once, anywhere. */
  confirmation: ReactNode;
}

/**
 * The server's own words for a refused mark: the day's, when it is the day
 * that is refused, and the refusal's otherwise.
 */
function refusalOf(error: Error): string {
  const fields =
    'fieldErrors' in error
      ? (error as { fieldErrors: Record<string, string[]> }).fieldErrors
      : {};
  return fields.deliveredAt?.[0] ?? failureWords(error);
}

/**
 * Mark received, the same on every screen that offers it. The request of
 * 5 October 2026 (third), section 11: the shipping screen, an archive row and
 * the case record all mark a case through this hook, so the question, the toast
 * and the way back cannot differ between them.
 *
 * Confirmation first: `ask` opens `DeliveredDialog`, the shared confirmation
 * with the question in plain words and an optional day and signer. The mark is
 * then sent with the order's rowVersion. A refusal is shown inside the sheet in
 * the server's words and the sheet stays, or as a toast when the sheet has been
 * closed in the meantime.
 *
 * Undo after: a toast says the case was marked and carries Undo for about ten
 * seconds. Undo takes the mark back by editing the shipping with the rowVersion
 * the marking answered with, so the server refuses it once anybody has changed
 * the case since, and says why. Undo is the way back from a mistake and is
 * never asked about. Later corrections go through Edit shipping, as before.
 *
 * Not `useOfferUndo`: that one is bound to a tray's undo, and a case that has
 * left the lab has none.
 */
export function useMarkReceived(): MarkReceived {
  const { data: session } = useSession();
  const mark = useMarkDelivered();
  const unmark = useUnmarkReceived();
  const { mutateAsync: sendMark } = mark;
  const { mutateAsync: sendUnmark } = unmark;

  const [target, setTarget] = useState<ReceivedCase | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);
  // The case whose sheet is open, for an answer that arrives after it was closed.
  const openFor = useRef<string | null>(null);
  // The marks on their way, so Enter and the button together send one.
  const sending = useRef(new Set<string>());

  useEffect(
    () => () => {
      openFor.current = null;
    },
    [],
  );

  const allowed =
    session !== undefined &&
    session !== null &&
    mayMarkReceived({
      role: session.role,
      views: session.views,
      permissions: session.permissions,
    });

  const ask = useCallback((next: ReceivedCase) => {
    openFor.current = next.jobOrderNo;
    setRefusal(null);
    setTarget(next);
  }, []);

  const close = useCallback(() => {
    openFor.current = null;
    setTarget(null);
  }, []);

  function send(input: { deliveredAt?: string; receivedBy?: string }) {
    if (target === null) return;
    const { jobOrderNo, rowVersion } = target;
    if (sending.current.has(jobOrderNo)) return;
    sending.current.add(jobOrderNo);
    setRefusal(null);

    /*
     * By the promise and not by callbacks on the call: the sheet may be closed
     * by its head while the mark is on its way, and the Undo must still be
     * offered for a mark that went through.
     */
    sendMark({ jobOrderNo, rowVersion, ...input }).then(
      (order) => {
        sending.current.delete(jobOrderNo);
        if (openFor.current === jobOrderNo) close();
        toast(`${jobOrderNo} ${strings.shipping.receivedSaved}`, {
          tone: 'success',
          durationMs: UNDO_OFFER_MS,
          action: {
            label: strings.undo.action,
            onClick: () => {
              // With the rowVersion the marking answered with: nobody has
              // touched the case since, or the server says so.
              sendUnmark({ jobOrderNo, rowVersion: order.rowVersion }).then(
                () => toast(strings.shipping.receivedUndone(jobOrderNo)),
                (error: Error) => toast(failureWords(error), { tone: 'error' }),
              );
            },
          },
        });
      },
      (error: Error) => {
        sending.current.delete(jobOrderNo);
        if (openFor.current === jobOrderNo) setRefusal(refusalOf(error));
        else toast(refusalOf(error), { tone: 'error' });
      },
    );
  }

  const confirmation =
    target === null ? null : (
      <DeliveredDialog
        // A sheet of its own for each case, so a day typed for one is never sent for another.
        key={target.jobOrderNo}
        jobOrderNo={target.jobOrderNo}
        dispatchedAt={target.dispatchedAt}
        // This case's own mark, not one still on its way for a sheet closed before it.
        busy={mark.isPending && mark.variables.jobOrderNo === target.jobOrderNo}
        refusal={refusal}
        onCancel={close}
        onConfirm={send}
      />
    );

  return { allowed, ask, confirmation };
}
